import CardWithAside from 'components/Card/CardWithAside';
import Input from 'components/Input';
import SearchInput from 'components/SearchInput';
import React from 'react';
import { HeroIconBuilding, useTextInput } from 'toolbox';
import { useMachine } from '@xstate/compiled/react';
import { editCurrencyBookExposureLimitMachine } from './EditCurrencyBookExposureLimits.machine';

interface EditCurrencyBookExposureLimitsProps {
  banks: {
    id: number;
    name: string;
    image?: { public_url?: string };
  }[];
  saveBankLimits: (banks: { id: number; limit: number }[]) => Promise<any>;
  exposureLimits: { [bankId: number]: { exposure_limit: number } };
  onSuccess: () => void;
  onError: () => void;
}

export const EditCurrencyBookExposureLimits: React.FC<EditCurrencyBookExposureLimitsProps> = (
  props,
) => {
  const { throttledValue, inputProps, setValue } = useTextInput();
  const [state, dispatch] = useMachine(editCurrencyBookExposureLimitMachine, {
    actions: {
      onError: props.onError,
      onSuccess: props.onSuccess,
    },
    services: {
      saveLimit: (context) => {
        return props.saveBankLimits(
          Object.entries(context.limitChanges).map(([id, { limit }]) => ({
            id: Number(id),
            limit: Number(limit),
          })),
        );
      },
    },
  });

  return (
    <CardWithAside
      title="Banks & Exposure Limits"
      aside={
        <>
          <SearchInput
            {...inputProps}
            placeholder="Filter"
            onResetClick={() => setValue('')}
          />
          <p className="mt-4 text-sm">
            Banks that are visible to this Currency Book, and associated
            Exposure Limits. Changes are applied immediately.
          </p>
          {state.matches('errored.aLimitIsAbove100') && (
            <p className="mt-2 text-sm text-red-600">
              Each exposure limit must be 100% or below
            </p>
          )}
          {state.matches('errored.aLimitIsBelow0') && (
            <p className="mt-2 text-sm text-red-600">
              Each exposure limit must be 0% or above
            </p>
          )}
          {state.matches('errored.aLimitIsNotANumber') && (
            <p className="mt-2 text-sm text-red-600">
              Each exposure limit must be a valid number
            </p>
          )}
        </>
      }
      children={
        <div className="flex flex-wrap -m-1 wrapper-fixed-height">
          {props.banks.length > 0 && throttledValue && (
            <p className="text-sm text-gray-600">
              No banks match your search text
            </p>
          )}
          {props.banks
            ?.filter((bank) =>
              bank?.name?.toLowerCase().includes(throttledValue.toLowerCase()),
            )
            ?.map((bank, i) => {
              return (
                <div
                  key={i}
                  className={`${'bg-jcap-100'} w-full mb-2 mr-2 py-2 pl-2 rounded flex items-center justify-between cursor-pointer`}
                >
                  <div className="inline-flex items-center w-8 h-8 mr-3">
                    {bank?.image?.public_url ? (
                      <img
                        src={bank?.image?.public_url}
                        alt={bank?.name}
                        title={bank?.name}
                      />
                    ) : (
                      <HeroIconBuilding className="w-full h-full p-2 fill-current text-jcap-500" />
                    )}
                  </div>
                  <div
                    className={`${'text-jcap-700'} flex-grow mr-2 font-semibold truncate select-none`}
                  >
                    {bank.name}
                  </div>
                  <div className="flex items-center justify-end flex-shrink-0 w-32 h-8 mr-4">
                    <>
                      <span className="mr-2 text-right text-2xs text-jcap-600">
                        Exposure Limit %
                      </span>
                      <Input
                        type="number"
                        min={0}
                        max={100}
                        step={1}
                        className="w-20 h-8 -mb-4 bg-white border border-gray-200 pointer-events-auto"
                        value={
                          state.context.limitChanges?.[bank.id]?.limit ??
                          props.exposureLimits?.[bank.id]?.exposure_limit
                        }
                        onChange={(e) => {
                          dispatch({
                            type: 'EDIT_LIMIT',
                            bankId: bank.id,
                            limit: e.target.value,
                          });
                        }}
                      />
                    </>
                  </div>
                </div>
              );
            })}
        </div>
      }
    />
  );
};

export default EditCurrencyBookExposureLimits;
